import {
  // ----- Types -----
  float,
  vec3,
  // ----- Math & Interpolation Nodes -----
  length,
  // ----- Control Flow Nodes -----
  Fn,
  If,
  // ----- Noise Functions -----
  mx_noise_float,
} from 'three/tsl';

// --------------------
//  TSL Helper Functions
// --------------------

/**
 * 3D Curl Noise計算 - Perlin Noiseを使用
 * 位置(pos)と時間(time)を受け取り、3次元のベクトルを返す
 * 3つのスカラー場から3Dベクトル場を生成
 */
export const computeCurl3D = Fn(([pos, time]: [ReturnType<typeof vec3>, ReturnType<typeof float>]) => {
  const epsilon = float(0.001); // 非常に小さな距離

  // ノイズのスケール調整
  const scale = float(0.005);
  const scaledPos = vec3(pos.x.mul(scale), pos.y.mul(scale), pos.z.mul(scale));
  const scaledTime = time.mul(0.1);

  // 3つのスカラー場のサンプル（異なるオフセットで）
  // 時間はz成分として使用（3Dノイズの時間軸）
  const offset1 = vec3(0.0, 0.0, 0.0);
  const offset2 = vec3(100.0, 0.0, 0.0);
  const offset3 = vec3(0.0, 100.0, 0.0);

  // Ψ1の偏微分を計算（yとz方向）
  // 時間はz成分に追加（3Dノイズの時間軸として使用）
  const pos1_y_plus = vec3(scaledPos.x.add(offset1.x), scaledPos.y.add(epsilon).add(offset1.y), scaledPos.z.add(offset1.z).add(scaledTime));
  const pos1_y_minus = vec3(scaledPos.x.add(offset1.x), scaledPos.y.sub(epsilon).add(offset1.y), scaledPos.z.add(offset1.z).add(scaledTime));
  const psi1_y = mx_noise_float(pos1_y_plus).sub(mx_noise_float(pos1_y_minus)).div(epsilon.mul(2.0));

  const pos1_z_plus = vec3(scaledPos.x.add(offset1.x), scaledPos.y.add(offset1.y), scaledPos.z.add(epsilon).add(offset1.z).add(scaledTime));
  const pos1_z_minus = vec3(scaledPos.x.add(offset1.x), scaledPos.y.add(offset1.y), scaledPos.z.sub(epsilon).add(offset1.z).add(scaledTime));
  const psi1_z = mx_noise_float(pos1_z_plus).sub(mx_noise_float(pos1_z_minus)).div(epsilon.mul(2.0));

  // Ψ2の偏微分を計算（xとz方向）
  const pos2_x_plus = vec3(scaledPos.x.add(epsilon).add(offset2.x), scaledPos.y.add(offset2.y), scaledPos.z.add(offset2.z).add(scaledTime));
  const pos2_x_minus = vec3(scaledPos.x.sub(epsilon).add(offset2.x), scaledPos.y.add(offset2.y), scaledPos.z.add(offset2.z).add(scaledTime));
  const psi2_x = mx_noise_float(pos2_x_plus).sub(mx_noise_float(pos2_x_minus)).div(epsilon.mul(2.0));

  const pos2_z_plus = vec3(scaledPos.x.add(offset2.x), scaledPos.y.add(offset2.y), scaledPos.z.add(epsilon).add(offset2.z).add(scaledTime));
  const pos2_z_minus = vec3(scaledPos.x.add(offset2.x), scaledPos.y.add(offset2.y), scaledPos.z.sub(epsilon).add(offset2.z).add(scaledTime));
  const psi2_z = mx_noise_float(pos2_z_plus).sub(mx_noise_float(pos2_z_minus)).div(epsilon.mul(2.0));

  // Ψ3の偏微分を計算（xとy方向）
  const pos3_x_plus = vec3(scaledPos.x.add(epsilon).add(offset3.x), scaledPos.y.add(offset3.y), scaledPos.z.add(offset3.z).add(scaledTime));
  const pos3_x_minus = vec3(scaledPos.x.sub(epsilon).add(offset3.x), scaledPos.y.add(offset3.y), scaledPos.z.add(offset3.z).add(scaledTime));
  const psi3_x = mx_noise_float(pos3_x_plus).sub(mx_noise_float(pos3_x_minus)).div(epsilon.mul(2.0));

  const pos3_y_plus = vec3(scaledPos.x.add(offset3.x), scaledPos.y.add(epsilon).add(offset3.y), scaledPos.z.add(offset3.z).add(scaledTime));
  const pos3_y_minus = vec3(scaledPos.x.add(offset3.x), scaledPos.y.sub(epsilon).add(offset3.y), scaledPos.z.add(offset3.z).add(scaledTime));
  const psi3_y = mx_noise_float(pos3_y_plus).sub(mx_noise_float(pos3_y_minus)).div(epsilon.mul(2.0));

  // 3D Curlの定義: (∂Ψ3/∂y - ∂Ψ2/∂z, ∂Ψ1/∂z - ∂Ψ3/∂x, ∂Ψ2/∂x - ∂Ψ1/∂y)
  const curlX = psi3_y.sub(psi2_z);
  const curlY = psi1_z.sub(psi3_x);
  const curlZ = psi2_x.sub(psi1_y);

  return vec3(curlX, curlY, curlZ);
});


/**
 * Mouse Force計算（3D版）
 * マウス位置を中心に360度方面に外力が働き、パーティクルが弾き飛ばされる
 * @param pos パーティクルの3D位置
 * @param mousePos マウスの3D位置
 * @returns パーティクルに加える3D反発力
 */
export const computeMouseForce3D = Fn(([pos, mousePos]: [ReturnType<typeof vec3>, ReturnType<typeof vec3>]) => {
  // パーティクルとマウスの距離を計算
  const diff = pos.sub(mousePos);
  const dist = length(diff);

  // マウスの影響範囲（3D空間単位）
  // SPACE_SIZE * 0.5 = 150.0 を超える範囲に設定して、3D空間全体に影響を与える
  const influenceRadius = float(300.0);
  // マウスの影響力の強さ
  const forceStrength = float(100.0);

  // 結果を格納する変数
  const result = vec3(0.0, 0.0, 0.0).toVar();

  // 距離が影響範囲内の場合のみ力を加える
  If(dist.lessThan(influenceRadius).and(dist.greaterThan(0.0)), () => {
    // 正規化された方向ベクトル（マウスからパーティクルへの方向 = 外向き）
    const dir = diff.div(dist);

    // 距離に応じた力の強さ（近いほど強い）
    // smoothstepを使って滑らかな減衰を実現
    const normalizedDist = dist.div(influenceRadius);
    const forceMagnitude = forceStrength.mul(float(1.0).sub(normalizedDist));

    // 反発力（マウスから離れる方向 = 360度外向き）
    result.assign(dir.mul(forceMagnitude));
  });

  return result;
});

